/**
 * Pure ffmpeg-command operations for audio tools -- the audio-category
 * equivalent of video/operations.ts. Same scope philosophy: the ffmpeg
 * lifecycle (loadFFmpeg/resetFFmpegEngine) and the input file write stay
 * in each component, since some tools (AudioTrimmer previewing, for
 * instance) interleave UI concerns with the file that operations.ts
 * shouldn't need to know about. What's extracted is the actual
 * command-building and execution for one operation.
 */
import type { FFmpeg } from "@ffmpeg/ffmpeg";
import { execWithProgress, probeMedia } from "@/lib/ffmpegEngine";
import { NO_AUDIO_TRACK_MESSAGE } from "./messages";
import { fadeFilter } from "./waveform";

export interface AudioFormatOption {
  value: string;
  label: string;
  codec: string[];
  mime: string;
}

/** Extracted from AudioConverter.tsx's FORMAT_OPTIONS -- the single
 * source of truth for which codec args and mime type each output
 * format needs, shared with any other tool that offers a format
 * choice. */
export const AUDIO_FORMAT_OPTIONS: AudioFormatOption[] = [
  { value: "mp3", label: "MP3", codec: ["-c:a", "libmp3lame", "-q:a", "2"], mime: "audio/mpeg" },
  { value: "wav", label: "WAV", codec: ["-c:a", "pcm_s16le"], mime: "audio/wav" },
  { value: "ogg", label: "OGG", codec: ["-c:a", "libvorbis", "-q:a", "5"], mime: "audio/ogg" },
  { value: "m4a", label: "M4A", codec: ["-c:a", "aac", "-b:a", "192k"], mime: "audio/mp4" },
  { value: "flac", label: "FLAC", codec: ["-c:a", "flac"], mime: "audio/flac" },
];

export type AudioQuality = "high" | "standard" | "small";
export const AUDIO_QUALITY_LABELS: Record<AudioQuality, string> = { high: "High (largest file)", standard: "Standard", small: "Small (smaller file)" };
/** Codec arguments for a format at a quality level; lossless formats (WAV, FLAC) ignore the level. */
export function codecForQuality(format: string, quality: AudioQuality): string[] {
  const base = AUDIO_FORMAT_OPTIONS.find((o) => o.value === format)!.codec;
  if (format === "mp3") return ["-c:a", "libmp3lame", "-q:a", { high: "2", standard: "4", small: "6" }[quality]];
  if (format === "ogg") return ["-c:a", "libvorbis", "-q:a", { high: "5", standard: "3", small: "1" }[quality]];
  if (format === "m4a") return ["-c:a", "aac", "-b:a", { high: "192k", standard: "128k", small: "96k" }[quality]];
  return base;
}

/** Audio length from the browser's own decoder (null when it can't be read). */
export function probeDuration(file: Blob): Promise<number | null> {
  return new Promise((resolve) => {
    const a = document.createElement("audio");
    const url = URL.createObjectURL(file);
    const done = (v: number | null) => { URL.revokeObjectURL(url); resolve(v); };
    a.preload = "metadata";
    a.onloadedmetadata = () => done(Number.isFinite(a.duration) && a.duration > 0 ? a.duration : null);
    a.onerror = () => done(null);
    setTimeout(() => done(null), 8000);
    a.src = url;
  });
}

const LOSSY_BITRATES = [32, 48, 64, 80, 96, 112, 128, 160, 192, 224, 256, 320];
/** Next real bitrate step at or below the given kbps (never above it, floor 32). */
export function stepAtOrBelow(kbps: number): number {
  let best = LOSSY_BITRATES[0];
  for (const b of LOSSY_BITRATES) if (b <= kbps + 1) best = b;
  return best;
}

/** Bitrate (kbps) that matches the source but never exceeds it; lossless/unknown sources return the "high" level. */
export function matchBitrate(file: { size: number; name: string }, durationSec: number | null): number {
  const lossless = /\.(wav|wave|flac|aiff?)$/i.test(file.name);
  if (lossless || !durationSec) return 192;
  return stepAtOrBelow((file.size * 8) / durationSec / 1000);
}

/** Label for the "Same as original" quality chip, stating the bitrate it will actually use. */
export function matchQualityLabel(file: { size: number; name: string }, durationSec: number | null): string {
  const lossless = /\.(wav|wave|flac|aiff?)$/i.test(file.name);
  const kbps = matchBitrate(file, durationSec);
  if (lossless) return `Same as original (lossless source, ${kbps} kbps)`;
  if (!durationSec) return `Same as original (${kbps} kbps, length unknown)`;
  return `Same as original (${kbps} kbps)`;
}

export function matchCodec(format: string, kbps: number): string[] {
  const k = `${kbps}k`;
  if (format === "mp3") return ["-c:a", "libmp3lame", "-b:a", k];
  if (format === "ogg") return ["-c:a", "libvorbis", "-b:a", k];
  if (format === "m4a") return ["-c:a", "aac", "-b:a", k];
  return AUDIO_FORMAT_OPTIONS.find((o) => o.value === format)!.codec;
}

/** Output format that keeps the input's own format (WAV stays WAV, FLAC stays FLAC, M4A/AAC -> M4A, OGG stays OGG),
 * MP3 for everything else (including video files). */
export function sameFormat(fileName: string): AudioFormatOption {
  const ext = (fileName.match(/\.([^.]+)$/)?.[1] ?? "").toLowerCase();
  const key = ext === "wav" || ext === "wave" ? "wav" : ext === "flac" ? "flac" : ["m4a", "aac", "mp4"].includes(ext) ? "m4a" : ["ogg", "oga", "opus"].includes(ext) ? "ogg" : "mp3";
  return AUDIO_FORMAT_OPTIONS.find((o) => o.value === key)!;
}

/** Largest input kept as uncompressed WAV between bundle steps; bigger ones use FLAC (lossless, about half the size). */
export const INTERMEDIATE_WAV_MAX_BYTES = 25 * 1024 * 1024;

/**
 * Output format for a tool. On its own it is the input's format (sameFormat). In the middle of a bundle it is a
 * lossless one (WAV, or FLAC for big files), so a lossy source is decoded once and encoded only by the last step,
 * instead of being re-encoded at every step.
 */
export function stepOutputFormat(file: { name: string; size: number }, intermediate: boolean): AudioFormatOption {
  const same = sameFormat(file.name);
  if (!intermediate || same.value === "wav" || same.value === "flac") return same;
  return AUDIO_FORMAT_OPTIONS.find((o) => o.value === (file.size > INTERMEDIATE_WAV_MAX_BYTES ? "flac" : "wav"))!;
}

export interface NormalizeOutput { format: AudioFormatOption; codec: string[]; /** One line for the result summary, or "" when nothing needs explaining. */ note: string }

/**
 * Output format and encoder settings for the loudness normalizer.
 * - ACX preset (on its own, not in the middle of a bundle): always a 192 kbps constant-bit-rate MP3 at 44.1 kHz, which is what ACX
 *   accepts, whatever the input was (a WAV in gives an MP3 out).
 * - Everything else: the input's own format; a lossy MP3/M4A/OGG is written at the source's bit rate (never below it, constant
 *   rate) instead of the converter's default quality setting, which for a 128 kbps file came out around 110 kbps.
 * - In the middle of a bundle: unchanged (lossless intermediate, encoded once by the last step).
 */
export function normalizeOutput(file: { name: string; size: number }, intermediate: boolean, targetId: string, durationSec: number | null): NormalizeOutput {
  if (!intermediate && targetId === "audiobook") {
    const mp3 = AUDIO_FORMAT_OPTIONS.find((o) => o.value === "mp3")!;
    return { format: mp3, codec: ["-ar", "44100", "-c:a", "libmp3lame", "-b:a", "192k", "-write_xing", "0"], note: "Saved as a 192 kbps constant-bit-rate MP3 at 44.1 kHz, the format ACX accepts." };
  }
  const format = stepOutputFormat(file, intermediate);
  if (!intermediate && ["mp3", "m4a", "ogg"].includes(format.value)) {
    const kbps = matchBitrate(file, durationSec);
    return { format, codec: matchCodec(format.value, kbps), note: durationSec ? `Saved at ${kbps} kbps, matched to the original.` : "" };
  }
  return { format, codec: format.codec, note: "" };
}

const LOSSY_FORMATS = ["mp3", "ogg", "m4a"];

/** Encoder arguments and the bitrate they give, for converting `file` to `format` at `quality` ("match" = the source's own bitrate). */
export function convertPlan(file: { size: number; name: string }, format: string, quality: AudioQuality | "match", durationSec: number | null): { codec: string[]; kbps: number | null; lossy: boolean } {
  const lossy = LOSSY_FORMATS.includes(format);
  if (quality === "match") return { codec: matchCodec(format, matchBitrate(file, durationSec)), kbps: lossy ? matchBitrate(file, durationSec) : null, lossy };
  const codec = codecForQuality(format, quality);
  const rate = Number((codec.find((a) => /k$/.test(a)) ?? "0k").replace("k", "")) || null;
  return { codec, kbps: lossy ? rate : null, lossy };
}

/** The line shown under a finished conversion, including the honest bitrate note ("the source is lossless, so 192 kbps was used"). */
export function describeConversion(file: { size: number; name: string }, outBytes: number, format: string, quality: AudioQuality | "match", durationSec: number | null, size: (bytes: number) => string): string {
  const opt = AUDIO_FORMAT_OPTIONS.find((f) => f.value === format)!;
  const lossy = LOSSY_FORMATS.includes(format);
  const detail = !lossy ? "" : ` (${quality === "match"
    ? `${matchBitrate(file, durationSec)} kbps${/\.(wav|wave|flac|aiff?)$/i.test(file.name) ? " (the source is lossless, so 192 kbps was used)" : ", matched to the original"}`
    : `${AUDIO_QUALITY_LABELS[quality].split(" ")[0].toLowerCase()} quality`})`;
  return `${file.name.replace(/^.*\./, "").toUpperCase()} ${size(file.size)} \u2192 ${opt.label} ${size(outBytes)}${detail}.`;
}

/** Name of the converted file. Same-format output must not reuse the original's exact name (it would overwrite or become "(1)"). */
export function convertedName(name: string, format: string): string {
  const same = new RegExp(`\\.${format}$`, "i").test(name);
  return same ? name.replace(/\.[^.]+$/, `-converted.${format}`) : name.replace(/\.[^.]+$/, `.${format}`);
}

/** Audio-only mapping (no cover picture "video" stream) keeping the tags. */
const AUDIO_ONLY = ["-map", "0:a:0", "-vn", "-map_metadata", "0"];

/**
 * Converts an already-written input file to a different audio
 * format/codec, extracted from AudioConverter.tsx.
 */
export async function convertAudioFormat(
  ffmpeg: FFmpeg,
  inputPath: string,
  outputPath: string,
  codecArgs: string[],
  onProgress: (fraction: number) => void,
  signal?: AbortSignal,
  durationSec?: number
): Promise<Uint8Array> {
  // Audio only: an embedded cover picture is a "video" stream, and without -map/-vn ffmpeg tries to encode
  // it into the output (it crashed for OGG). Tags are kept with -map_metadata 0.
  const audioOnly = ["-map", "0:a:0", "-vn", "-map_metadata", "0"];
  const coverCapable = /\.(mp3|m4a|flac)$/i.test(outputPath);
  if (coverCapable) {
    // Keep the album art where the format supports it; if that fails for an odd cover, redo without it.
    try {
      await execWithProgress(ffmpeg, ["-i", inputPath, "-map", "0:a:0", "-map", "0:v:0?", "-c:v", "copy", "-disposition:v", "attached_pic", "-map_metadata", "0", ...codecArgs, outputPath], onProgress, signal, durationSec);
      return ffmpeg.readFile(outputPath) as Promise<Uint8Array>;
    } catch (err) {
      if (signal?.aborted) throw err;
    }
  }
  await execWithProgress(ffmpeg, ["-i", inputPath, ...audioOnly, ...codecArgs, outputPath], onProgress, signal, durationSec);
  return ffmpeg.readFile(outputPath) as Promise<Uint8Array>;
}

/**
 * Converts one file's bytes to `format` at `quality`, using that file's own bitrate for "same as original" (so each
 * file of a batch gets its own). Writes the input itself, so it can be called once per file in a loop; the caller
 * owns the engine's lifecycle. Used by the single-file and the several-files modes of the Audio Converter.
 */
export async function convertAudioWithPlan(
  ffmpeg: FFmpeg,
  file: { name: string; size: number },
  bytes: Uint8Array,
  format: string,
  quality: AudioQuality | "match",
  durationSec: number | null,
  onProgress: (fraction: number) => void,
  signal?: AbortSignal
): Promise<Uint8Array> {
  const inExt = file.name.match(/\.([^.]+)$/)?.[1] || "mp3";
  await ffmpeg.writeFile(`input.${inExt}`, bytes);
  return convertAudioFormat(ffmpeg, `input.${inExt}`, `output.${format}`, convertPlan(file, format, quality, durationSec).codec, onProgress, signal, durationSec ?? undefined);
}

export { formatMinSec, formatMinSecTenth, formatDuration } from "./timeFormat";

/** Parses "1:23", "83", or "1:23.5" into seconds, extracted from
 * AudioTrimmer.tsx -- forgiving of exactly how someone types a time,
 * since asking for a strict format is its own friction. Returns null
 * for unparseable input rather than throwing, so callers can show a
 * validation message instead of crashing. */
export function parseMinSec(input: string): number | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  if (trimmed.includes(":")) {
    const [m, s] = trimmed.split(":");
    const minutes = parseFloat(m);
    const seconds = parseFloat(s);
    if (Number.isNaN(minutes) || Number.isNaN(seconds)) return null;
    return minutes * 60 + seconds;
  }
  const seconds = parseFloat(trimmed);
  return Number.isNaN(seconds) ? null : seconds;
}

/**
 * Cuts an already-written input file down to [startSeconds,
 * endSeconds], extracted from AudioTrimmer.tsx. Re-encodes rather than
 * stream-copying (unlike video's TrimVideo) -- this is a deliberate,
 * documented trade-off in the original tool: it means the cut point is
 * exact rather than snapped to the nearest keyframe, at the cost of a
 * little real processing time.
 */
export async function trimAudioSegment(
  ffmpeg: FFmpeg,
  inputPath: string,
  outputPath: string,
  startSeconds: number,
  endSeconds: number,
  onProgress: (fraction: number) => void,
  signal?: AbortSignal,
  opts: { codec?: string[]; fadeIn?: number; fadeOut?: number; /** Extra ffmpeg filter applied before the fades, e.g. a volume boost. */ preFilter?: string } = {}
): Promise<Uint8Array> {
  const { codec = [], fadeIn = 0, fadeOut = 0, preFilter = "" } = opts;
  const af = [preFilter, fadeFilter(fadeIn, fadeOut, endSeconds - startSeconds)].filter(Boolean).join(",");
  // -t is the length of the clip; -to would be read against the output clock (which starts at 0 after -ss) and cut or silence any clip that does not start at 0.
  // -ss goes BEFORE -i (input-side seek): an output-side -ss combined with the fade filter and the cover-art map gave silence for any start past about 10 s in testing (RT-1).
  const seek = ["-ss", String(startSeconds)];
  const common = ["-t", String(Math.round((endSeconds - startSeconds) * 1000) / 1000)];
  if (/\.(mp3|m4a|flac)$/i.test(outputPath)) {
    // Keep the album art where the format supports it; fall back to audio only if the cover cannot be copied.
    try {
      await execWithProgress(ffmpeg, [...seek, "-i", inputPath, ...common, "-map", "0:a:0", "-map", "0:v:0?", "-c:v", "copy", "-disposition:v", "attached_pic", "-map_metadata", "0", ...(af ? ["-af", af] : []), ...codec, outputPath], onProgress, signal);
      return ffmpeg.readFile(outputPath) as Promise<Uint8Array>;
    } catch (err) { if (signal?.aborted) throw err; }
  }
  await execWithProgress(ffmpeg, [...seek, "-i", inputPath, ...common, "-map", "0:a:0", "-vn", "-map_metadata", "0", ...(af ? ["-af", af] : []), ...codec, outputPath], onProgress, signal);
  return ffmpeg.readFile(outputPath) as Promise<Uint8Array>;
}

/** Length of the fade-out / fade-in put either side of a cut so the join cannot click. */
export const JOIN_FADE_SECONDS = 0.008;

export interface RemovalPlan {
  /** Parts of the clip that stay, in order, as [from, to] seconds. */
  keep: Array<[number, number]>;
  /** Length of what is left. */
  outSeconds: number;
  /** Set when nothing sensible can be done (the message is for the person). */
  error?: string;
}

/**
 * What "remove this section" leaves: the part before the section and the part after it. A section that starts at
 * the very beginning or ends at the very end leaves a single part; one that covers everything is an error.
 */
export function planRemoval(durationSeconds: number, startSeconds: number, endSeconds: number): RemovalPlan {
  const start = Math.max(0, startSeconds);
  const end = Math.min(durationSeconds, endSeconds);
  if (!(durationSeconds > 0)) return { keep: [], outSeconds: 0, error: "The length of this audio is not known yet." };
  if (end <= start) return { keep: [], outSeconds: 0, error: "The end of the section must be after its start." };
  const keep: Array<[number, number]> = [];
  if (start > 0.005) keep.push([0, start]);
  if (end < durationSeconds - 0.005) keep.push([end, durationSeconds]);
  const outSeconds = keep.reduce((n, [a, b]) => n + (b - a), 0);
  if (outSeconds < 0.05) return { keep: [], outSeconds: 0, error: "That would remove everything, so nothing would be left. Choose a smaller section." };
  return { keep, outSeconds };
}

/**
 * ffmpeg filter graph that keeps only the given ranges of the first audio stream and joins them, with a few
 * milliseconds of fade either side of each join. A range that ends at `totalSeconds` runs to the end of the stream
 * (so a duration the browser rounded cannot clip the last samples). The result is on the label [out]; `after` (for
 * example the user's fades) is applied to the joined audio.
 */
export function keepRangesFilter(keep: Array<[number, number]>, totalSeconds: number, after = ""): string {
  const parts = keep.map(([a, b], i) => {
    const len = b - a;
    const f = Math.min(JOIN_FADE_SECONDS, len / 2);
    const fades = [
      i > 0 ? `afade=t=in:st=0:d=${f}` : "",
      i < keep.length - 1 ? `afade=t=out:st=${Math.max(0, len - f).toFixed(4)}:d=${f}` : "",
    ].filter(Boolean);
    const trim = b >= totalSeconds - 0.005 ? `atrim=start=${a}` : `atrim=start=${a}:end=${b}`;
    return `[0:a:0]${[trim, "asetpts=PTS-STARTPTS", ...fades].join(",")}[k${i}]`;
  });
  const chain = keep.length > 1
    ? `${keep.map((_, i) => `[k${i}]`).join("")}concat=n=${keep.length}:v=0:a=1${after ? "," + after : ""}[out]`
    : `[k0]${after || "anull"}[out]`;
  return `${parts.join(";")};${chain}`;
}

/**
 * Deletes a section from an already-written input file and joins what is left ("remove selection", the opposite of
 * trimAudioSegment, which keeps one range). The cut is exact (the audio is decoded and re-encoded). Fades apply to
 * the start and end of the joined result.
 */
export async function removeAudioSegment(
  ffmpeg: FFmpeg,
  inputPath: string,
  outputPath: string,
  durationSeconds: number,
  startSeconds: number,
  endSeconds: number,
  onProgress: (fraction: number) => void,
  signal?: AbortSignal,
  opts: { codec?: string[]; fadeIn?: number; fadeOut?: number } = {}
): Promise<{ data: Uint8Array; outSeconds: number }> {
  const { codec = [], fadeIn = 0, fadeOut = 0 } = opts;
  const plan = planRemoval(durationSeconds, startSeconds, endSeconds);
  if (plan.error) throw new Error(plan.error);
  const filter = keepRangesFilter(plan.keep, durationSeconds, fadeFilter(fadeIn, fadeOut, plan.outSeconds));
  if (/\.(mp3|m4a|flac)$/i.test(outputPath)) {
    // Keep the album art where the format supports it; fall back to audio only if the cover cannot be copied.
    try {
      await execWithProgress(ffmpeg, ["-i", inputPath, "-filter_complex", filter, "-map", "[out]", "-map", "0:v:0?", "-c:v", "copy", "-disposition:v", "attached_pic", "-map_metadata", "0", ...codec, outputPath], onProgress, signal);
      return { data: (await ffmpeg.readFile(outputPath)) as Uint8Array, outSeconds: plan.outSeconds };
    } catch (err) { if (signal?.aborted) throw err; }
  }
  await execWithProgress(ffmpeg, ["-i", inputPath, "-filter_complex", filter, "-map", "[out]", "-map_metadata", "0", ...codec, outputPath], onProgress, signal);
  return { data: (await ffmpeg.readFile(outputPath)) as Uint8Array, outSeconds: plan.outSeconds };
}

/** Thrown when a video has no audio stream, so there is nothing to extract. */
export class NoAudioTrackError extends Error {
  constructor() { super(NO_AUDIO_TRACK_MESSAGE); this.name = "NoAudioTrackError"; }
}

/** File type and mime for each extracted-audio extension. */
export const EXTRACT_MIME: Record<string, string> = { mp3: "audio/mpeg", wav: "audio/wav", m4a: "audio/mp4", ogg: "audio/ogg", opus: "audio/ogg", flac: "audio/flac" };

/**
 * Output file type and encoder arguments for "Save as" in Extract Audio. "original" copies the audio exactly as it
 * is in the video (no re-encode, instant) when its codec has a plain file type (`acodec` is ffprobe's codec name);
 * otherwise it falls back to MP3 and says so. mp3-128 and mp3-192 are fixed bitrates; the rest use the high setting.
 */
export function extractCodec(fmt: string, acodec: string): { ext: string; codec: string[]; fellBack: boolean } {
  const cbr = /^mp3-(\d+)$/.exec(fmt);
  if (fmt === "original") {
    const copyExt: Record<string, string> = { aac: "m4a", mp3: "mp3", opus: "opus", vorbis: "ogg", flac: "flac" };
    const ce = copyExt[acodec];
    return ce ? { ext: ce, codec: ["-c:a", "copy"], fellBack: false } : { ext: "mp3", codec: AUDIO_FORMAT_OPTIONS[0].codec, fellBack: true };
  }
  if (cbr) return { ext: "mp3", codec: ["-c:a", "libmp3lame", "-b:a", `${cbr[1]}k`], fellBack: false };
  return { ext: fmt, codec: codecForQuality(fmt, "high"), fellBack: false };
}

/**
 * Takes the audio track out of a video's bytes. Looks at the file first and throws NoAudioTrackError when the video
 * has no sound (so a silent video can never end in a button that did nothing). `range` limits it to [start, end] seconds.
 * Writes its own input, so it can be called once per video in a loop; the caller owns the engine's lifecycle.
 */
export async function extractAudioFromVideo(
  ffmpeg: FFmpeg,
  bytes: Uint8Array,
  fmt: string,
  range: [number, number] | null,
  onProgress: (fraction: number) => void,
  signal?: AbortSignal
): Promise<{ data: Uint8Array; ext: string; mime: string; fellBack: boolean }> {
  await ffmpeg.writeFile("input", bytes);
  const probe = await probeMedia(ffmpeg, "input");
  if (!probe.hasAudio) throw new NoAudioTrackError();
  const { ext, codec, fellBack } = extractCodec(fmt, probe.acodec);
  const seek = range ? ["-ss", String(range[0])] : [];
  const length = range ? ["-t", String(Math.round((range[1] - range[0]) * 1000) / 1000)] : [];
  await execWithProgress(ffmpeg, [...seek, "-i", "input", ...length, "-vn", "-map", "0:a:0", ...codec, `output.${ext}`], onProgress, signal);
  return { data: (await ffmpeg.readFile(`output.${ext}`)) as Uint8Array, ext, mime: EXTRACT_MIME[ext] || "audio/mpeg", fellBack };
}

/** Longest gap or crossfade offered between clips, in seconds. */
export const MAX_JOIN_SECONDS = 5;

/**
 * The crossfade that will really be used at each join (one entry per pair of neighbouring clips). A crossfade
 * cannot be longer than half of either clip, or the clips would be eaten up by it, so a long crossfade on a short
 * clip is shortened. An unknown clip length does not limit it.
 */
export function crossfadeDurations(durations: Array<number | null>, crossfadeSeconds: number): number[] {
  const out: number[] = [];
  for (let i = 0; i + 1 < durations.length; i++) {
    const limit = Math.min(...[durations[i], durations[i + 1]].map((d) => (d && d > 0 ? d / 2 : Infinity)));
    out.push(Math.max(0, Math.round(Math.min(crossfadeSeconds, limit) * 1000) / 1000));
  }
  return out;
}

/** Length of the merged file: the clips added up, less each crossfade, plus each gap. Null when a clip's length is unknown. */
export function expectedMergeSeconds(durations: Array<number | null>, gapSeconds: number, crossfadeSeconds: number): number | null {
  if (durations.length === 0 || durations.some((d) => !d)) return null;
  const sum = (durations as number[]).reduce((a, b) => a + b, 0);
  if (crossfadeSeconds > 0) return sum - crossfadeDurations(durations, crossfadeSeconds).reduce((a, b) => a + b, 0);
  return sum + gapSeconds * (durations.length - 1);
}

/**
 * ffmpeg filter graph for joining `n` inputs. Every clip is first resampled to `sampleRate` (when given), so
 * clips recorded at different rates are all brought to one and keep their pitch. Between clips: nothing, a gap of
 * silence (apad), or a crossfade (acrossfade, equal-power curves) of the given length at each join. A gap and a
 * crossfade are alternatives: when crossfades are given, gapSeconds is ignored. The result is on [outa].
 */
export function buildMergeFilter(n: number, opts: { gapSeconds?: number; crossfades?: number[]; sampleRate?: number | null } = {}): string {
  const { gapSeconds = 0, crossfades = [], sampleRate = null } = opts;
  const prep = Array.from({ length: n }, (_, i) => `[${i}:a]${sampleRate ? `aresample=${sampleRate}` : "anull"}[s${i}]`);
  const fading = crossfades.some((d) => d > 0);
  if (fading) {
    const joins: string[] = [];
    let prev = "[s0]";
    for (let i = 1; i < n; i++) {
      const label = i === n - 1 ? "[outa]" : `[x${i}]`;
      joins.push(`${prev}[s${i}]acrossfade=d=${crossfades[i - 1] ?? 0}:c1=qsin:c2=qsin${label}`);
      prev = label;
    }
    return [...prep, ...joins].join(";");
  }
  const pads = Array.from({ length: n }, (_, i) => (gapSeconds > 0 && i < n - 1 ? `[s${i}]apad=pad_dur=${gapSeconds}[p${i}]` : null));
  const refs = Array.from({ length: n }, (_, i) => (pads[i] ? `[p${i}]` : `[s${i}]`)).join("");
  return [...prep, ...pads.filter(Boolean), `${refs}concat=n=${n}:v=0:a=1[outa]`].join(";");
}

/** "1st", "2nd", "3rd", "4th"... */
export function ordinal(n: number): string {
  const v = n % 100;
  return `${n}${v >= 11 && v <= 13 ? "th" : ["th", "st", "nd", "rd"][n % 10] ?? "th"}`;
}
const kHz = (rate: number) => `${+(rate / 1000).toFixed(2)}`;

/** The rate every clip is brought to: the first clip's own rate (the first one whose rate is known). */
export function mergeSampleRate(rates: Array<number | null>): number | null {
  return rates.find((r) => !!r) ?? null;
}

/**
 * One line saying which clips were resampled and from what, or "" when every clip already had the target rate
 * (or its rate is unknown): "2nd clip resampled 48 -> 44.1 kHz".
 */
export function resampleNote(rates: Array<number | null>, target: number | null): string {
  if (!target) return "";
  const parts = rates.flatMap((r, i) => (r && r !== target ? [`${ordinal(i + 1)} clip resampled ${kHz(r)} \u2192 ${kHz(target)} kHz`] : []));
  return parts.join("; ");
}

/** A name for the merged file that says what it is: merged-2-clips.wav. */
export function mergedFileName(clipCount: number, ext: string): string {
  return `merged-${clipCount}-${clipCount === 1 ? "clip" : "clips"}.${ext}`;
}

/**
 * Merges multiple audio files into one, extracted from AudioMerger.tsx.
 * Owns writing its own input files, same reasoning as video's
 * mergeVideos -- a merge inherently needs several inputs at once.
 * Unlike mergeVideos, there's no fallback path needed here: audio
 * concat doesn't have video's "clip is missing a stream" failure mode,
 * since every input here is audio-only by definition.
 *
 * Between clips: `gapSeconds` of silence, or a crossfade (`mix.crossfadeSeconds`, shortened where a clip is too
 * short, see crossfadeDurations). `mix.sampleRate` is the one rate all clips are brought to.
 */
export async function mergeAudioFiles(
  ffmpeg: FFmpeg,
  files: File[],
  fetchFile: (file: File) => Promise<Uint8Array>,
  onProgress: (fraction: number) => void,
  signal?: AbortSignal,
  gapSeconds = 0,
  /** Output container + encoder arguments; MP3 high quality when omitted. */
  out: { ext: string; codec: string[]; durationSec?: number } = { ext: "mp3", codec: ["-c:a", "libmp3lame", "-q:a", "2"] },
  mix: { crossfadeSeconds?: number; sampleRate?: number | null; durations?: Array<number | null> } = {}
): Promise<Uint8Array> {
  const inputArgs: string[] = [];
  for (let i = 0; i < files.length; i++) {
    const ext = (files[i].name.match(/\.([A-Za-z0-9]+)$/)?.[1] ?? "mp3").toLowerCase();
    await ffmpeg.writeFile(`in${i}.${ext}`, await fetchFile(files[i]));
    inputArgs.push("-i", `in${i}.${ext}`);
  }
  const crossfades = (mix.crossfadeSeconds ?? 0) > 0 ? crossfadeDurations(mix.durations ?? files.map(() => null), mix.crossfadeSeconds!) : [];
  const filterComplex = buildMergeFilter(files.length, { gapSeconds, crossfades, sampleRate: mix.sampleRate });
  await execWithProgress(
    ffmpeg,
    [...inputArgs, "-filter_complex", filterComplex, "-map", "[outa]", ...out.codec, `output.${out.ext}`],
    onProgress,
    signal,
    out.durationSec
  );
  return ffmpeg.readFile(`output.${out.ext}`) as Promise<Uint8Array>;
}

export const MIN_BITRATE_KBPS = 32;

/** Calculates the bitrate needed to hit a target file-size percentage
 * of the original, extracted from AudioCompressor.tsx. Audio bitrate
 * encoding lands close enough to a requested bitrate that a single
 * calculated pass is reliable here -- unlike video compression, which
 * genuinely needs iterative search (see pdf/operations.ts's
 * compressVideoToTarget for that different case). */
export function calculateTargetBitrateKbps(fileSizeBytes: number, targetPercent: number, durationSeconds: number): number {
  const targetBytes = fileSizeBytes * targetPercent;
  const targetKbps = (targetBytes * 8) / durationSeconds / 1000;
  return Math.max(MIN_BITRATE_KBPS, Math.round(targetKbps));
}

/**
 * Re-encodes an already-written input file at a specific bitrate,
 * extracted from AudioCompressor.tsx.
 */
export async function compressAudioToBitrate(
  ffmpeg: FFmpeg,
  inputPath: string,
  outputPath: string,
  bitrateKbps: number,
  onProgress: (fraction: number) => void,
  signal?: AbortSignal,
  mono = false
): Promise<Uint8Array> {
  await execWithProgress(
    ffmpeg,
    ["-i", inputPath, "-map", "0:a:0", "-vn", "-map_metadata", "0", ...(mono ? ["-ac", "1"] : []), "-c:a", "libmp3lame", "-b:a", `${bitrateKbps}k`, outputPath],
    onProgress,
    signal
  );
  return ffmpeg.readFile(outputPath) as Promise<Uint8Array>;
}

export interface NormalizeTarget {
  id: string;
  label: string;
  hint: string;
  /** Integrated loudness in LUFS, or null for "peak" mode. */
  lufs: number | null;
  /** True-peak ceiling in dB (or the peak target in peak mode). */
  peak: number;
}

export const NORMALIZE_TARGETS: NormalizeTarget[] = [
  { id: "podcast", label: "Podcast (-16 LUFS)", hint: "-16 LUFS, a common podcast level", lufs: -16, peak: -1.5 },
  { id: "spotify", label: "Spotify / streaming (-14 LUFS)", hint: "-14 LUFS, the level Spotify normalises to", lufs: -14, peak: -1 },
  { id: "youtube", label: "YouTube (common streaming reference)", hint: "-14 LUFS, a common streaming reference (YouTube publishes no official target)", lufs: -14, peak: -1 },
  { id: "broadcast", label: "Broadcast (EBU R128)", hint: "-23 LUFS", lufs: -23, peak: -1 },
  { id: "audiobook", label: "Audiobook (ACX)", hint: "about -20 dB RMS (LUFS used as a proxy), peak -3 dB. Saved as a 192 kbps CBR MP3", lufs: -20, peak: -3 },
  { id: "peak", label: "Peak to -1 dB", hint: "just louder, no compression", lufs: null, peak: -1 },
];

/** Runs ffmpeg and returns its log lines (ffmpeg prints its measurements there). */
async function execCollectLog(ffmpeg: FFmpeg, args: string[], signal?: AbortSignal): Promise<string[]> {
  const lines: string[] = [];
  const handler = ({ message }: { message: string }) => { lines.push(message); };
  ffmpeg.on("log", handler);
  try {
    const code = await ffmpeg.exec(args, undefined, signal ? { signal } : undefined);
    if (signal?.aborted) throw new Error("Processing stopped");
    if (code !== 0) throw new Error(`Processing failed (exit code ${code}).`);
  } finally {
    ffmpeg.off("log", handler);
  }
  return lines;
}

export interface LoudnormMeasure { input_i: string; input_tp: string; input_lra: string; input_thresh: string; target_offset: string; }

/** Pulls the JSON block that loudnorm prints with print_format=json out of ffmpeg's log. */
export function parseLoudnorm(lines: string[]): LoudnormMeasure | null {
  const text = lines.join("\n");
  const m = text.match(/\{[^{}]*"input_i"[^{}]*\}/);
  if (!m) return null;
  try {
    const j = JSON.parse(m[0]);
    const n = (v: string) => Number.isFinite(Number(v)) && Math.abs(Number(v)) < 1000;
    return n(j.input_i) && n(j.input_tp) && n(j.input_lra) && n(j.input_thresh) && n(j.target_offset) ? j : null;
  } catch { return null; }
}

export function parseMaxVolume(lines: string[]): number | null {
  const m = lines.join("\n").match(/max_volume:\s*(-?[\d.]+) dB/);
  return m ? Number(m[1]) : null;
}

/**
 * Normalizes an already-written input file's loudness, extracted from
 * AudioNormalizer.tsx. Two passes: the first measures the file (EBU R128
 * loudnorm analysis), the second applies exactly the gain needed with
 * linear=true, which is more accurate and consistent than one dynamic pass
 * (it does not pump on very quiet or very dynamic material). Peak mode just
 * applies gain so the loudest sample sits at the target peak. Returns the
 * file and the measured input level in LUFS (null in peak mode).
 */
export interface NormalizeResult { data: Uint8Array; inputLufs: number | null; outputLufs: number | null; gainDb: number; limited: boolean; inputPeak: number | null; outputPeak: number | null }

/** ffmpeg -af chain for a gain, with an optional peak limiter so the gain can exceed the headroom without clipping. */
export function normalizeFilter(gainDb: number, ceilingDb: number, limiter: boolean): string {
  const vol = `volume=${gainDb.toFixed(3)}dB`;
  if (!limiter) return vol;
  const limit = Math.min(1, Math.max(0.0625, Math.pow(10, ceilingDb / 20)));
  // level=0 turns off alimiter's automatic make-up gain; the limiter only catches peaks above the ceiling.
  return `${vol},alimiter=limit=${limit.toFixed(4)}:attack=5:release=50:level=0`;
}

export async function normalizeAudioLoudness(
  ffmpeg: FFmpeg,
  inputPath: string,
  outputPath: string,
  target: NormalizeTarget,
  onProgress: (fraction: number) => void,
  signal?: AbortSignal,
  codec: string[] = [],
  /** Catch loud peaks with a limiter so a loudness target can be reached even when the peaks are high. */
  limiter = false
): Promise<NormalizeResult> {
  const lossy = codec.some((c) => /mp3lame|aac|vorbis/.test(c));
  // Encoding to a lossy format can nudge peaks up a little, so keep a small margin there.
  const margin = lossy ? 0.3 : 0.05;
  const encode = async (gainDb: number) => {
    await execWithProgress(ffmpeg, ["-i", inputPath, ...AUDIO_ONLY, "-af", normalizeFilter(gainDb, target.peak - margin, limiter && target.lufs !== null), ...codec, outputPath], onProgress, signal);
  };

  if (target.lufs === null) {
    const lines = await execCollectLog(ffmpeg, ["-i", inputPath, "-af", "volumedetect", "-f", "null", "-"], signal);
    const max = parseMaxVolume(lines);
    if (max === null) throw new Error("Processing failed: could not measure the level of this file.");
    const gain = target.peak - margin - max;
    await encode(gain);
    const out = parseMaxVolume(await execCollectLog(ffmpeg, ["-i", outputPath, "-af", "volumedetect", "-f", "null", "-"], signal));
    return { data: (await ffmpeg.readFile(outputPath)) as Uint8Array, inputLufs: null, outputLufs: null, gainDb: gain, limited: false, inputPeak: max, outputPeak: out };
  }

  const measure = async (path: string) => parseLoudnorm(await execCollectLog(ffmpeg, ["-i", path, "-af", "loudnorm=I=-23:TP=-1:LRA=11:print_format=json", "-f", "null", "-"], signal));
  const m = await measure(inputPath);
  if (!m) throw new Error("Processing failed: could not measure the loudness of this file.");
  const inLufs = Number(m.input_i);
  const inTp = Number(m.input_tp);
  const headroom = target.peak - margin - inTp;
  let gain = target.lufs - inLufs;
  // With the limiter on, the gain is not held back by the peaks: the limiter handles them.
  const useLimiter = limiter;
  let limited = !useLimiter && gain > headroom;
  if (limited) gain = headroom;
  await encode(gain);
  // Measure what was actually written (after encoding), and correct once if it landed away from the target.
  let outM = await measure(outputPath);
  let actual = outM ? Number(outM.input_i) : null;
  if (actual !== null && !limited && Math.abs(actual - target.lufs) > 0.25) {
    let g2 = gain + (target.lufs - actual);
    if (!useLimiter && g2 > headroom) { g2 = headroom; limited = true; }
    gain = g2;
    await encode(gain);
    outM = await measure(outputPath);
    actual = outM ? Number(outM.input_i) : actual;
  }
  if (useLimiter && actual !== null && actual < target.lufs - 0.5) limited = true; // even the limiter could not get there
  return { data: (await ffmpeg.readFile(outputPath)) as Uint8Array, inputLufs: inLufs, outputLufs: actual, gainDb: gain, limited, inputPeak: inTp, outputPeak: outM ? Number(outM.input_tp) : null };
}

/** FFmpeg's atempo filter only accepts 0.5-2.0 per instance, but can be
 * chained for values outside that range -- not needed by any current
 * caller since every UI offering this caps at 0.5-2x, but documented
 * here in case that range ever changes. Extracted from
 * AudioSpeedChanger.tsx. */
export function atempoFilter(speed: number): string {
  // atempo accepts 0.5-2.0 per instance, so faster/slower speeds are chained (3x = 2 x 1.5, 4x = 2 x 2).
  const parts: number[] = [];
  let left = speed;
  while (left > 2) { parts.push(2); left /= 2; }
  while (left < 0.5) { parts.push(0.5); left /= 0.5; }
  parts.push(Math.round(left * 10000) / 10000);
  return parts.map((v) => `atempo=${v}`).join(",");
}

/**
 * ffmpeg filter chain for the speed changer. Speed alone is atempo (pitch unchanged). With a pitch shift as well
 * the clip is first pitch-shifted at its own length (see pitchShiftFilters) and then sped up or slowed down, so
 * speed and pitch are two independent controls. `sampleRate` must be the rate of the input: the chain resamples to
 * it first and ends at it, so the output keeps the input's sample rate.
 */
export function speedPitchFilter(speed: number, semitones: number, sampleRate: number): string {
  const tempo = Math.abs(speed - 1) > 1e-6 ? atempoFilter(speed) : "";
  if (!semitones) return tempo || "anull";
  return [pitchShiftFilters(semitones, sampleRate), tempo].filter(Boolean).join(",");
}

/**
 * Speeds up or slows down an already-written input file, extracted from AudioSpeedChanger.tsx. The pitch stays
 * as it was unless `pitch` asks for a shift in semitones (then `sampleRate` is the input's rate).
 */
export async function changeAudioSpeed(
  ffmpeg: FFmpeg,
  inputPath: string,
  outputPath: string,
  speed: number,
  onProgress: (fraction: number) => void,
  signal?: AbortSignal,
  codec: string[] = [],
  pitch?: { semitones: number; sampleRate: number }
): Promise<Uint8Array> {
  const filter = pitch && pitch.semitones ? speedPitchFilter(speed, pitch.semitones, pitch.sampleRate) : atempoFilter(speed);
  await execWithProgress(ffmpeg, ["-i", inputPath, ...AUDIO_ONLY, "-filter:a", filter, ...codec, outputPath], onProgress, signal);
  return ffmpeg.readFile(outputPath) as Promise<Uint8Array>;
}

/** Shifts pitch by `semitones` while keeping the original duration,
 * extracted from AudioScrambler.tsx. asetrate changes the sample rate
 * (shifting pitch AND speed together, like a tape running faster or
 * slower), then atempo compensates the speed back to 1x while the
 * pitch shift stays -- a well-established ffmpeg technique for this
 * exact effect. atempo only accepts 0.5-2.0 per instance; the semitone
 * range this is used with (-6 to +6) never needs more than that in one
 * step. */
export function pitchShiftFilters(semitones: number, sampleRate: number): string {
  const rateFactor = Math.pow(2, semitones / 12);
  const newRate = Math.round(sampleRate * rateFactor);
  const tempoCompensation = 1 / rateFactor;
  // aresample first: asetrate must be built on the rate the filter actually receives, so the input is
  // forced to `sampleRate` (phones and video files are 48 kHz, not 44.1 kHz).
  return `aresample=${sampleRate},asetrate=${newRate},atempo=${tempoCompensation},aresample=${sampleRate}`;
}

/**
 * Shifts an already-written input file's pitch, extracted from
 * AudioScrambler.tsx. The input is resampled to 44100 Hz first, so the
 * pitch and length are right whatever the source rate was.
 */
export async function shiftAudioPitch(
  ffmpeg: FFmpeg,
  inputPath: string,
  outputPath: string,
  semitones: number,
  onProgress: (fraction: number) => void,
  signal?: AbortSignal,
  codec: string[] = [],
  /** Only process the first N seconds (a quick preview). */
  limitSeconds = 0
): Promise<Uint8Array> {
  await execWithProgress(ffmpeg, ["-i", inputPath, ...AUDIO_ONLY, "-af", pitchShiftFilters(semitones, 44100), ...(limitSeconds > 0 ? ["-t", String(limitSeconds)] : []), ...codec, outputPath], onProgress, signal);
  return ffmpeg.readFile(outputPath) as Promise<Uint8Array>;
}

/**
 * Reduces steady background noise in an already-written input file,
 * extracted from NoiseRemover.tsx. afftdn is ffmpeg's FFT-based denoiser
 * (spectral subtraction). `reductionDb` is nr, how many dB of noise to
 * remove. `noiseFloorDb` is nf (-80..-20), the level the filter treats as
 * noise; it only removes what sits at or below it, so it is set from the
 * file's measured noise floor (see noise.ts), not a fixed number.
 */
export async function removeAudioNoise(
  ffmpeg: FFmpeg,
  inputPath: string,
  outputPath: string,
  reductionDb: number,
  noiseFloorDb: number,
  onProgress: (fraction: number) => void,
  signal?: AbortSignal,
  codec: string[] = [],
  /** RNNoise model bytes: when given, the neural denoiser (arnndn) is used instead of spectral subtraction. */
  rnnModel?: Uint8Array
): Promise<Uint8Array> {
  let filter = `afftdn=nr=${reductionDb}:nf=${noiseFloorDb}`;
  if (rnnModel) {
    await ffmpeg.writeFile("voice.rnnn", rnnModel);
    filter = "aresample=48000,arnndn=m=voice.rnnn:mix=1";
  }
  await execWithProgress(ffmpeg, ["-i", inputPath, ...AUDIO_ONLY, "-af", filter, ...codec, outputPath], onProgress, signal);
  return ffmpeg.readFile(outputPath) as Promise<Uint8Array>;
}
